// The New delivery panel's booking rules as pure functions: which quote answer is current, what a booking answer
// means, the panel state machine, which request a booking sends, and every footer label and notice. Fail closed:
// anything the server did not clearly answer is "unknown" (the carrier may hold a booking), never success and never
// a clean refusal, and nothing is ever booked at a price staff did not see and confirm.
import { formatCents } from "./manualShipment";
import { countdownText } from "./manualDeliveryTime";
import { sentence } from "./manualDeliveryStatus";
import { shipmentKey } from "./manualDeliveryForm";
import { SESSION_EXPIRED } from "./manualDeliveries";

export const QUOTE_TTL_MS = 15 * 60 * 1000;
// A quote with less than this left is refreshed before Review, so staff never confirm a price about to lapse.
export const REVIEW_MIN_SECONDS = 60;

const NETWORK_ERROR =
  "We couldn't reach the server for prices. Check the connection; prices load again when it is back.";

// Quote answers. Every quotes call gets a rising seq; only the answer to the newest call is applied, so an older
// call that finishes late can never put an old price and its token back on screen.
export const initialQuotesState = {
  seq: 0,
  pendingKey: null,
  key: null,
  options: null,
  error: null,
  receivedAt: 0,
  // Counts every answer applied, priced or failed, so a caller can tell a new answer from the one before.
  answered: 0,
};

export function quotesReducer(state, action) {
  switch (action.type) {
    case "request":
      // key null: quoting is off (the form is incomplete); any call still out is dropped when it answers.
      return { ...state, seq: action.seq, pendingKey: action.key };
    case "answer": {
      if (action.seq !== state.seq) return state;
      const { result } = action;
      if (
        result &&
        result.ok &&
        result.data &&
        Array.isArray(result.data.options)
      ) {
        return {
          ...state,
          pendingKey: null,
          key: action.key,
          options: result.data.options,
          error: null,
          receivedAt: action.receivedAt,
          answered: state.answered + 1,
        };
      }
      // The last options stay on screen as "updating"; key null means none of them is current.
      const error =
        result && result.data && result.data.error
          ? result.data.error
          : NETWORK_ERROR;
      return {
        ...state,
        pendingKey: null,
        key: null,
        error,
        answered: state.answered + 1,
      };
    }
    default:
      return state;
  }
}

// True only when the options answer exactly this shipment and no newer call is out.
export function quotesFresh(state, key) {
  return !!key && state.pendingKey === null && state.key === key;
}

// An option staff can book: the own driver always (it may have no price), a courier only with a price and the
// signed token for it. An unavailable courier, retryable or not, is never bookable.
export function isBookable(option) {
  if (!option || option.status !== "ok") return false;
  if (option.kind === "own") return true;
  return Number.isInteger(option.price_cents) && !!option.quote_id;
}

// result: { ok, status, data } from postForResult, or null when the request itself failed (network, timeout).
// A 401 stopped at sign-in, so nothing reached the carrier: "notSent", a plain failed start staff can retry after
// signing in. 403, 404 and 5xx are "unknown": they are not one of the answers the booking contract defines. A 2xx is
// success only with booking evidence: the row (id or manual_delivery.id) or the delivery ("already scheduled"). A
// 2xx without it (a proxy page postForResult read as {}) cannot tell whether anything was booked.
export function bookingOutcome(result) {
  if (!result) return { kind: "unknown", reason: "network", row: null };
  const data = result.data || {};
  const kept =
    data.manual_delivery && data.manual_delivery.id
      ? data.manual_delivery
      : null;
  if (result.ok) {
    if (kept || data.id) return { kind: "success", row: kept || data };
    if (data.delivery) return { kind: "success", row: null };
    return { kind: "unknown", reason: "unexpected", row: null };
  }
  if (result.status === 401) {
    return { kind: "notSent", message: data.error || SESSION_EXPIRED, row: null };
  }
  const quote = data.quote;
  if (
    result.status === 409 &&
    quote &&
    Number.isInteger(quote.price_cents) &&
    (quote.quote_id || kept)
  ) {
    return { kind: "priceChanged", quote, row: kept };
  }
  if (result.status === 422) {
    return {
      kind: "refused",
      message: data.error || "The booking was not accepted.",
      row: kept,
    };
  }
  if (result.status === 423)
    return { kind: "unknown", reason: "locked", row: kept };
  return { kind: "unknown", reason: "unexpected", row: kept };
}

// The option staff are confirming, frozen together with the exact shipment it was priced for. Booking always
// sends offer.shipment and offer.quoteId, never a shipment rebuilt from the form. null for an option that is not
// bookable, so it can never reach the review step.
export function offerFromOption(option, shipment, receivedAtMs) {
  if (!isBookable(option)) return null;
  return {
    optionId: option.id,
    kind: option.kind,
    carrierId: option.carrier_id,
    provider: option.provider || null,
    name: option.name,
    priceCents: Number.isInteger(option.price_cents)
      ? option.price_cents
      : null,
    quoteId: option.quote_id || null,
    expiresAtMs:
      option.kind === "own"
        ? null
        : receivedAtMs + (option.expires_in || 0) * 1000,
    details: option.details || null,
    labels: !!option.labels,
    dashboardUrl: option.dashboard_url || null,
    signatureForced: !!option.signature_forced,
    shipment,
    shipmentKey: shipmentKey(shipment),
  };
}

// Seconds left on a courier price hold (0 once expired, or when nowMs is not a time), null for the own driver.
export function secondsLeft(offer, nowMs) {
  if (!offer || offer.expiresAtMs === null) return null;
  const left = Math.floor((offer.expiresAtMs - nowMs) / 1000);
  return Number.isFinite(left) ? Math.max(0, left) : 0;
}

// Review pressed on a courier price about to lapse: what the panel waits on while useQuotes refreshes it. answered
// is the quotes answer count at the press, so the answer being replaced (still fresh until the new request goes out)
// is never reused.
export function reviewWait(option, shipment, answered) {
  return {
    optionId: option.id,
    priceCents: option.price_cents,
    key: shipmentKey(shipment),
    answered,
  };
}

// What to do with a Review that waits on a fresh price. view: the panel's current shipment, choice and quotes.
// "review" carries an offer built from the new answer (its own token and hold), never from the old one; "changed"
// means staff must look at the new answer and press Review again; "drop" ends the wait without a message (an edit
// started new prices, or the answer failed and the footer shows that error).
export function reviewAfterRefresh(wait, view) {
  if (shipmentKey(view.shipment) !== wait.key || view.choice !== wait.optionId) {
    return { type: "drop" };
  }
  if (view.loading || view.answered === wait.answered) return { type: "wait" };
  // A failed answer: the footer already shows its error.
  if (!view.fresh) return { type: "drop" };
  const option = (view.options || []).find((o) => o.id === wait.optionId);
  const offer = offerFromOption(option, view.shipment, view.receivedAt);
  if (
    offer &&
    offer.priceCents === wait.priceCents &&
    secondsLeft(offer, view.nowMs) >= REVIEW_MIN_SECONDS
  ) {
    return { type: "review", offer };
  }
  return { type: "changed" };
}

export const initialPanelState = {
  step: "details",
  phase: "idle",
  tried: false,
  offer: null,
  oldPriceCents: null,
  savedRowId: null,
  message: null,
  reason: null,
  earlierAttempt: false,
};

// Phases where nothing new may start: a booking is out, it may have booked (unknown), or it did.
const LOCKED = ["booking", "unknown", "done"];
const BOOKABLE = ["idle", "priceChanged", "refused"];

// The one gate for sending a booking. The panel calls it before the request (with its in-flight ref) and the
// reducer applies it to bookStart. A courier needs a price, a token (or the row the server kept, which the
// server re-prices) and a hold that has not run out.
export function canBook(state, nowMs) {
  const { offer } = state;
  if (state.step !== "review" || !offer || !BOOKABLE.includes(state.phase)) {
    return false;
  }
  if (offer.kind === "own") return true;
  if (!Number.isInteger(offer.priceCents)) return false;
  if (!state.savedRowId && !offer.quoteId) return false;
  return secondsLeft(offer, nowMs) > 0;
}

// 0 (already expired) when nowMs is not a time, so the footer asks for a new price instead of a dead button.
function expiryFrom(quote, nowMs) {
  if (!Number.isFinite(nowMs)) return 0;
  const at = Date.parse(quote.expires_at || "");
  return Number.isNaN(at)
    ? nowMs + QUOTE_TTL_MS
    : Math.min(at, nowMs + QUOTE_TTL_MS);
}

function applyOutcome(state, outcome, nowMs) {
  const savedRowId =
    outcome.row && outcome.row.id ? outcome.row.id : state.savedRowId;
  switch (outcome.kind) {
    case "success":
      return { ...state, phase: "done", savedRowId };
    case "priceChanged":
      // Never falls back to the old token: it is for a price staff no longer see.
      return {
        ...state,
        phase: "priceChanged",
        savedRowId,
        oldPriceCents: state.offer.priceCents,
        message: null,
        offer: {
          ...state.offer,
          priceCents: outcome.quote.price_cents,
          quoteId: outcome.quote.quote_id || null,
          details: outcome.quote.details || state.offer.details,
          expiresAtMs: expiryFrom(outcome.quote, nowMs),
        },
      };
    case "notSent":
      // Nothing was sent: the same offer can be booked again as it is.
      return { ...state, phase: "idle", savedRowId, message: outcome.message };
    case "refused":
      return {
        ...state,
        phase: "refused",
        savedRowId,
        message: outcome.message,
      };
    default:
      return {
        ...state,
        phase: "unknown",
        savedRowId,
        reason: outcome.reason || "unexpected",
        message: null,
      };
  }
}

export function panelReducer(state, action) {
  switch (action.type) {
    case "reset":
      return { ...initialPanelState };
    case "tried":
      return state.tried ? state : { ...state, tried: true };
    case "review":
      if (!action.offer || LOCKED.includes(state.phase)) return state;
      return {
        ...state,
        step: "review",
        phase: "idle",
        offer: action.offer,
        oldPriceCents: null,
        message: null,
      };
    case "edit":
      // After an unknown outcome the only way out is Save and close: going back could book it a second time.
      if (LOCKED.includes(state.phase)) return state;
      return {
        ...state,
        step: "details",
        phase: "idle",
        offer: null,
        oldPriceCents: null,
        message: null,
        // A refused row stays in the list; booking again from here creates another delivery.
        earlierAttempt: state.earlierAttempt || state.savedRowId !== null,
        savedRowId: null,
      };
    case "bookStart":
      if (!canBook(state, action.nowMs)) return state;
      return { ...state, phase: "booking", message: null };
    case "bookResult":
      if (state.phase !== "booking") return state;
      return applyOutcome(state, action.outcome, action.nowMs);
    case "requoted":
      // Only a new price for the shipment under review; a late answer after Edit details is dropped.
      if (
        state.step !== "review" ||
        !state.offer ||
        LOCKED.includes(state.phase) ||
        !action.offer ||
        action.offer.shipmentKey !== state.offer.shipmentKey
      ) {
        return state;
      }
      return {
        ...state,
        phase: "idle",
        message: null,
        oldPriceCents:
          action.offer.priceCents !== state.offer.priceCents
            ? state.offer.priceCents
            : state.oldPriceCents,
        offer: action.offer,
      };
    case "requoteFailed":
      if (state.step !== "review" || !state.offer) return state;
      return { ...state, message: action.message };
    default:
      return state;
  }
}

// Call only after canBook(state, now).
export function bookingRequest(state) {
  const { offer } = state;
  if (offer.kind === "own") return { type: "own" };
  if (state.savedRowId) {
    return {
      type: "retry",
      rowId: state.savedRowId,
      priceCents: offer.priceCents,
    };
  }
  return {
    type: "create",
    body: {
      carrier_id: offer.carrierId,
      quote_id: offer.quoteId,
      shipment: offer.shipment,
    },
  };
}

const price = (cents) =>
  Number.isInteger(cents) ? formatCents(cents) : "No price";

function detailsFooter(input) {
  const {
    option,
    fresh,
    quoting,
    quoteError,
    errors,
    tried,
    earlierAttempt,
    refreshing,
    refreshed,
  } = input;
  const errorCount = Object.keys(errors || {}).length;
  const own = !!option && option.kind === "own";
  const blocked = !!option && !isBookable(option);
  const noPrice = blocked && !!option.retryable;
  let sub = "";
  if (own) {
    sub = Number.isInteger(option.price_cents)
      ? "Store fee"
      : "Store fee unavailable";
  } else if (option && (quoting || !fresh)) sub = "Updating price…";
  else if (noPrice) sub = "Couldn't get a price";
  else if (blocked) sub = "Can't take this delivery";
  else if (option) sub = "Estimate";

  let notice = null;
  if (tried && errorCount) {
    notice = {
      tone: "danger",
      title:
        errorCount === 1
          ? "1 field needs fixing"
          : `${errorCount} fields need fixing`,
      text: "Fix the highlighted fields to see the exact price.",
      jump: { label: "Go to first problem", action: "jump" },
    };
  } else if (tried && blocked) {
    const reason = sentence(option.reason);
    notice = {
      tone: "danger",
      title: noPrice
        ? `Couldn't get a price from ${option.name}`
        : `${option.name} can't take this delivery`,
      text: [
        reason,
        noPrice
          ? "Try again in a moment or pick another carrier."
          : "Pick another carrier or change the details.",
      ]
        .filter(Boolean)
        .join(" "),
      jump: { label: "Go to carriers", action: "jumpCarriers" },
    };
  } else if (refreshing) {
    notice = {
      tone: "info",
      title: "Getting a fresh price…",
      text: "The last one was about to run out. Review opens when it is ready.",
    };
  } else if (quoteError) {
    notice = { tone: "warn", title: "Prices didn't load", text: quoteError };
  } else if (refreshed) {
    notice = {
      tone: "info",
      title: "The price was refreshed",
      text: "Check it, then press Review again.",
    };
  } else if (earlierAttempt) {
    notice = {
      tone: "info",
      title: "The earlier attempt stays in the list",
      text: "Cancel it there if you book this delivery another way.",
    };
  }

  return {
    title: "New delivery",
    left: option
      ? `${option.name} · ${price(option.price_cents)}`
      : "Pick a carrier",
    sub,
    subTone: blocked ? "danger" : "muted",
    notice,
    secondary: { label: "Cancel", action: "close" },
    primary: {
      label: "Review",
      action: "review",
      // Wait for the price of exactly this shipment; with errors, Review stays clickable so it can show them.
      disabled: errorCount === 0 && !!option && !own && (quoting || !fresh),
    },
  };
}

function unknownNotice(offer, reason, savedRowId) {
  const { name } = offer;
  const ref = savedRowId ? `MD-${savedRowId}` : "this delivery";
  const where = offer.dashboardUrl
    ? `the ${name} dashboard`
    : `your ${name} account`;
  const opener =
    reason === "locked"
      ? `${name} didn't confirm the booking.`
      : "We lost contact before the booking finished.";
  return {
    tone: "warn",
    title: `We can't tell if ${name} booked this`,
    text: `${opener} Don't book again yet; it may already be booked.`,
    steps: [
      `Open ${where} and search for ${ref}.`,
      `If it is there, don't book again. A manager cancels it with ${name} before anything else.`,
      "If it is not, a manager clears the attempt from the list after 15 minutes. Then you can book again.",
    ],
  };
}

function reviewFooter(input) {
  const {
    phase,
    offer,
    secondsLeft: left,
    oldPriceCents,
    message,
    reason,
    savedRowId,
    busy,
  } = input;
  const own = offer.kind === "own";
  const { name } = offer;
  const shown = price(offer.priceCents);
  const base = {
    title: own ? "Review delivery" : "Review and book",
    left: `${name} · ${shown}`,
    sub: "",
    subTone: "muted",
    notice: null,
    secondary: { label: "Edit details", action: "edit" },
  };

  if (phase === "booking") {
    return {
      ...base,
      sub: "Don't close this panel",
      secondary: null,
      primary: {
        label: own ? "Saving…" : `Booking with ${name}…`,
        action: "book",
        disabled: true,
      },
    };
  }
  if (phase === "unknown" && own) {
    // No carrier: the only question is whether the row was saved, and only the list can answer it.
    return {
      ...base,
      sub: "Not confirmed",
      subTone: "warn",
      notice: {
        tone: "warn",
        title: "We couldn't confirm the delivery was saved",
        text: "It may already be in the list. Check the list before adding it again.",
      },
      secondary: null,
      primary: { label: "Close", action: "saveClose", disabled: false },
    };
  }
  if (phase === "unknown") {
    return {
      ...base,
      sub: "Booking blocked until checked",
      subTone: "warn",
      notice: unknownNotice(offer, reason, savedRowId),
      secondary: offer.dashboardUrl
        ? { label: `Open ${name} dashboard`, action: "openDashboard" }
        : null,
      primary: {
        label: "Save and close",
        action: "saveClose",
        disabled: false,
      },
    };
  }
  // Same rule as canBook: a courier price whose hold ran out is never booked, whatever the phase.
  if (!own && left === 0) {
    return {
      ...base,
      sub: "Price expired",
      subTone: "warn",
      notice: {
        tone: "warn",
        title: "This price expired",
        text: message
          ? `${sentence(message)} Try again in a moment.`
          : "Courier prices are held for 15 minutes. Get a fresh price to book.",
      },
      primary: { label: "Get new price", action: "requote", disabled: !!busy },
    };
  }
  if (phase === "refused") {
    return {
      ...base,
      sub: own ? "Not saved" : "Not booked",
      subTone: "danger",
      notice: own
        ? {
            tone: "danger",
            title: "The delivery wasn't saved",
            text: `${sentence(message)} Check the details and try again.`,
          }
        : {
            tone: "danger",
            title: `${name} refused the booking`,
            text: `${sentence(
              message
            )} Nothing was booked. Check the details or try another carrier.`,
          },
      secondary: {
        label: own ? "Edit details" : "Change carrier",
        action: "edit",
      },
      primary: {
        label: own ? "Try again" : "Retry booking",
        action: "book",
        disabled: !!busy,
      },
    };
  }

  let notice = null;
  if (phase === "priceChanged") {
    let direction = "is now";
    if (Number.isInteger(oldPriceCents) && oldPriceCents < offer.priceCents) {
      direction = "went up to";
    }
    if (Number.isInteger(oldPriceCents) && oldPriceCents > offer.priceCents) {
      direction = "went down to";
    }
    notice = {
      tone: "warn",
      title: `${name}'s price ${direction} ${shown}`,
      text:
        "Nothing was booked. Confirm the new price to book, or go back and pick another carrier.",
    };
  } else if (message) {
    // A start that never got past sign-in (or a failed save of the own driver's row): nothing was sent.
    notice = {
      tone: "danger",
      title: own ? "The delivery wasn't saved" : "Not booked",
      text: sentence(message),
    };
  }
  let label = `Book with ${name}`;
  if (own) label = "Save delivery";
  else if (phase === "priceChanged") label = `Book at ${shown}`;
  return {
    ...base,
    sub: own ? "" : `Price held for ${countdownText(left)}`,
    subTone: !own && left < 60 ? "danger" : "muted",
    notice,
    primary: { label, action: "book", disabled: !!busy || phase === "done" },
  };
}

export function footerModel(input) {
  return input.step === "details" ? detailsFooter(input) : reviewFooter(input);
}

export function successToast(offer, { name, when }) {
  if (offer.kind === "own") {
    return {
      title: "Delivery saved for your driver",
      text: `${name} · ${when}`,
      printLabel: false,
    };
  }
  const shown = Number.isInteger(offer.priceCents)
    ? ` · ${formatCents(offer.priceCents)}`
    : "";
  return {
    title: `Booked with ${offer.name}${shown}`,
    text: `${name} · ${when}`,
    printLabel: !!offer.labels,
  };
}
